import { transitions } from '@leadsales/design-system/motion';
import { useEffect, useState } from 'react';
import { tModalPresenceProps } from './ModalPresence.types';

// How long a closing modal stays mounted: the overlay exit that
// src/styles/vendor/daisyui.motion.scss gives a closed .modal (DES-219).
// Reduced motion keeps it: the modal fades in place in the same time (DES-210).
export const MODAL_EXIT_MS = Math.round(
  transitions.overlayExit.duration * 1000,
);

/**
 * Mounts a modal that only exists while open ({open && <Modal />}) and keeps
 * it mounted for its exit (DES-222). The modal reads the same flag for its own
 * `open`, so during the exit it renders closed and daisyUI's .modal fades out.
 * Reopening during the exit keeps the same instance and reverses the fade.
 */
export default function ModalPresence(props: tModalPresenceProps) {
  const { open, children } = props;
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    if (!mounted) return undefined;
    const timer = window.setTimeout(() => setMounted(false), MODAL_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  if (!open && !mounted) return null;

  // A leaving modal is inert from its first frame: out of the tab order and
  // the pointer, so a focused button inside it cannot fire twice. React 18 has
  // no `inert` prop, so it goes as an attribute; React 19 takes it as a
  // boolean (`inert: true`). `contents` keeps the wrapper out of the layout.
  const exitProps = open ? {} : { 'data-exiting': '', inert: '' };
  return (
    <div className="contents" {...exitProps}>
      {children}
    </div>
  );
}
